// ─── areaFormUtils.js ─────────────────────────────────────────────────────────
// Pure utility functions shared by InternalAreaForm and ExternalAreaForm.

import { useCallback } from 'react';
import { getSurfaceConfig } from './areaConstants';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export const IS_DESKTOP = () => window.innerWidth >= 768;

// ── Empty data factories ──────────────────────────────────────────────────────

export function emptyEntry(surfaceName, baseSurface) {
  const base = baseSurface || surfaceName;
  const c = getSurfaceConfig(base);
  const e = { id: `${Date.now()}_${Math.random().toString(36).slice(2)}`, substrate: surfaceName, label: '', repeatGroupId: null };
  if (baseSurface) e.baseSurface = baseSurface;
  if (c.fields.includes('length'))      e.length = '';
  if (c.fields.includes('width'))       e.width = '';
  if (c.fields.includes('height'))      e.height = '';
  if (c.fields.includes('quantity'))    e.quantity = '1';
  if (c.fields.includes('doorWidth'))   e.doorWidth = '0.9';
  if (c.fields.includes('doorHeight'))  e.doorHeight = '2.1';
  if (c.fields.includes('sides'))       e.sides = 'one';
  if (c.fields.includes('frameWidth'))  e.frameWidth = '';
  if (c.fields.includes('frameLength')) e.frameLength = '';
  if (c.isWindows) { e.windowMeasureType = 'area'; e.girth = ''; e.includeFrame = false; e.windowFrameWidth = ''; }
  if (c.isDoors)                        e.includeFrame = false;
  if (c.hasLanding) { e.includeLanding = false; e.landingLength = ''; e.landingWidth = ''; }
  return e;
}

export function emptyInternal() {
  return { type: 'internal', name: '', tag: '', photos: [], surfaceEntries: [], surfaceSettings: {}, repeatGroups: [], itemOrder: [], downtime: 10, scopeNotes: '', scopeExclusions: '', defectsNote: '', accessNeeds: [], wrapModes: {}, preliminaryItems: [] };
}

export function emptyExternal() {
  return { type: 'external', name: '', tag: '', photos: [], surfaceEntries: [], surfaceSettings: {}, repeatGroups: [], itemOrder: [], downtime: 10, scopeNotes: '', scopeExclusions: '', defectsNote: '', accessNeeds: [], wrapModes: {}, preliminaryItems: [] };
}

// Legacy alias — external form still uses this
export function emptyExtEntry(substrate) {
  return emptyEntry(substrate);
}

// ── Auto-advance focus on Enter ───────────────────────────────────────────────

export function useAutoAdvance(containerRef) {
  return useCallback((e) => {
    if (e.key !== 'Enter') return;
    const inputs = Array.from(
      containerRef.current?.querySelectorAll('input[type="number"], input[type="text"]') || []
    );
    const idx = inputs.indexOf(e.target);
    if (idx >= 0 && idx < inputs.length - 1) {
      e.preventDefault();
      inputs[idx + 1].focus();
      inputs[idx + 1].select();
    }
  }, [containerRef]);
}

// ── Photo compression ─────────────────────────────────────────────────────────
// Adapted from Surfacely's photoCompression.js pattern.
// Uses OffscreenCanvas first (Chrome/Firefox — avoids DOM, no layout triggers),
// then falls back to regular canvas (Safari).
// createImageBitmap decodes off-thread; falls back to new Image() for Safari.

async function loadImageBitmap(fileOrBlob) {
  try {
    return await createImageBitmap(fileOrBlob);
  } catch {
    // Safari: createImageBitmap may reject some blob types
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(fileOrBlob);
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
      img.src = url;
    });
  }
}

async function resizeToBlob(img, maxDim, quality) {
  let w = img.width;
  let h = img.height;
  if (w > maxDim || h > maxDim) {
    const ratio = Math.min(maxDim / w, maxDim / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }

  // OffscreenCanvas first — no DOM interaction, faster on Chrome/Firefox
  if (typeof OffscreenCanvas !== 'undefined') {
    try {
      const oc = new OffscreenCanvas(w, h);
      oc.getContext('2d').drawImage(img, 0, 0, w, h);
      return await oc.convertToBlob({ type: 'image/jpeg', quality });
    } catch { /* fall through */ }
  }

  // Regular canvas fallback (Safari, older browsers)
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob() returned null'))),
      'image/jpeg',
      quality
    );
  });
}

// Compress one blob to full-size + thumbnail in parallel.
// Returns { fullBlob, thumbnailBlob }.
async function compressPhoto(fileOrBlob) {
  const img = await loadImageBitmap(fileOrBlob);
  const [fullBlob, thumbnailBlob] = await Promise.all([
    resizeToBlob(img, 1280, 0.72),   // Full: 1280px, 72% quality
    resizeToBlob(img, 300,  0.65),   // Thumbnail: 300px, 65% quality
  ]);
  if (typeof img.close === 'function') img.close(); // release ImageBitmap memory
  return { fullBlob, thumbnailBlob };
}

// ── uploadPhoto ───────────────────────────────────────────────────────────────
// Accepts a raw File or Blob from a native <input type="file"> picker.
// Shows a placeholder immediately, compresses + uploads both sizes in background.
// onPreview(tempPhoto)              — called instantly with local blob URL + pending flag
// onComplete(tempId, { url, thumbnailUrl }) — called when both uploads finish
// onError(tempId)                   — called if upload fails
export function uploadPhoto(file, projectId, onPreview, onComplete, onError) {
  const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const localUrl = URL.createObjectURL(file);

  // Show placeholder immediately
  if (onPreview) {
    onPreview({ id: tempId, url: localUrl, pending: true,
      uploadedAt: new Date().toISOString(), isProjectHero: false, isDefect: false,
      substrate: null, isPrimaryForSubstrate: false });
  }

  // Both uploads run entirely in background — nothing awaited by caller
  (async () => {
    try {
      const safeName = (file.name || 'photo.jpg').replace(/[^a-zA-Z0-9.]/g, '_');
      const basePath = `photos/${projectId}/${Date.now()}_${safeName}`;
      const thumbPath = basePath.replace(/(\.[^.]+)?$/, '_thumb$1');

      const { fullBlob: compressed, thumbnailBlob: thumbBlob } = await compressPhoto(file);

      const [url, thumbnailUrl] = await Promise.all([
        uploadBytes(ref(storage, basePath), compressed).then(() => getDownloadURL(ref(storage, basePath))),
        uploadBytes(ref(storage, thumbPath), thumbBlob).then(() => getDownloadURL(ref(storage, thumbPath))),
      ]);

      URL.revokeObjectURL(localUrl);
      if (onComplete) onComplete(tempId, { url, thumbnailUrl });
    } catch (err) {
      URL.revokeObjectURL(localUrl);
      console.error('Photo upload failed:', err);
      if (onError) onError(tempId);
    }
  })();

  return tempId;
}
